import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useFirestoreConnect, isLoaded } from 'react-redux-firebase';
import moment from 'moment';
import { useWindowSize } from '@react-hook/window-size';

import { useTheme } from '../../ThemeContext';

import { userEditInit, userEdit } from '../../actions/User/UserActions';

import ProfileManageAsideTabs from '../ProfileManageAsideTabs';
import ProfileManageUsersInviteUsers from '../ProfileManageUsersInviteUsers';
import ProfileManageUsersManageUser from '../ProfileManageUsersManageUser';
import LoadingData from '../LoadingData';
import NoResults from '../NoResults';
import Select from '../Select';

import {
  Header,
  Title,
  Content,
  Footer,
  Button,
  Box,
  Aside,
  Main,
  MainInner,
  Subtitle,
  Table,
  Row,
  Cell,
  Label,
  Value,
  DateLabel,
  ActionButton,
  ActionButtonIcon,
  Strong,
  MoreBar,
  More,
  MoreIcon,
  AvatarHolder,
  AvatarPlaceholder,
  Avatar,
  Inputbox,
  Input,
  Deco,
  SortSign,
  SortSignIcon,
} from './ProfileManageUsers.style';

import { Breakpoints } from '../../assets/Variables.style';

import roles from '../../roles';

const ProfileManageUsers = ({
  currentTab,
  tabs = [],
  handleClose = () => {},
  handleChangeTab = () => {},
}) => {
  const [width] = useWindowSize();
  const intl = useIntl();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { auth, profile } = useSelector((state) => state.firebase);
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const profileCompany = useSelector(
    (state) => state.firestore.data['profileCompany']
  );
  const profileCompanyRole =
    profileCompany &&
    profileCompany.admins &&
    profileCompany.admins.length > 0 &&
    profileCompany.admins.find((admin) => admin.id === auth.uid)
      ? 'isAdmin'
      : profileCompany &&
        profileCompany.users &&
        profileCompany.users.length > 0 &&
        profileCompany.users.find((user) => user.id === auth.uid)
      ? 'isUser'
      : undefined;
  const authRole = roles.find(
    (role) => profileCompanyRole && role.id === profileCompanyRole
  );
  const profileCompanyUsers = useSelector(
    (state) => state.firestore.ordered['profileCompanyUsers']
  );
  const [defaultProfileCompanyUsersLimit] = useState(undefined);
  const [
    currentProfileCompanyUsersLimit,
    setCurrentProfileCompanyUsersLimit,
  ] = useState(defaultProfileCompanyUsersLimit);
  const [
    currentProfileCompanyUsersSortName,
    setCurrentProfileCompanyUsersSortName,
  ] = useState(undefined);
  const [
    currentProfileCompanyUsersSortType,
    setCurrentProfileCompanyUsersSortType,
  ] = useState(undefined);

  const [
    visibleProfileManageUsersInviteUsersModal,
    setVisibleProfileManageUsersInviteUsersModal,
  ] = useState(false);
  const [
    visibleProfileManageUsersManageUserModal,
    setVisibleProfileManageUsersManageUserModal,
  ] = useState(false);
  const [selectedUser, setSelectedUser] = useState(undefined);

  const handleProfileCompanyUsersLoadingMoreData = () => {
    setCurrentProfileCompanyUsersLimit(
      currentProfileCompanyUsersLimit + defaultProfileCompanyUsersLimit
    );
  };

  const handleChangeRole = (user, role) => {
    try {
      dispatch(
        userEdit({
          id: user.id,
          company: user.company && user.company.id,
          role: role
            ? role === 'isAdmin'
              ? 'admins'
              : role === 'isUser'
              ? 'users'
              : undefined
            : undefined,
        })
      );
    } catch {}
  };

  const handleSetCurrentSort = (name) => {
    if (currentProfileCompanyUsersSortName !== name) {
      setCurrentProfileCompanyUsersSortName(name);
      setCurrentProfileCompanyUsersSortType('asc');
    } else
      setCurrentProfileCompanyUsersSortType(
        currentProfileCompanyUsersSortType === null
          ? 'asc'
          : currentProfileCompanyUsersSortType === 'asc'
          ? 'desc'
          : null
      );
  };

  const handleAction = (action = {}) => {
    if (action.type === 'INVITE') {
      setVisibleProfileManageUsersInviteUsersModal(true);
    } else if (action.type === 'DELETE') {
      setVisibleProfileManageUsersManageUserModal(true);
      setSelectedUser(action.payload);
    } else {
      setVisibleProfileManageUsersInviteUsersModal(false);
      setVisibleProfileManageUsersManageUserModal(false);
      setSelectedUser(undefined);
    }
  };

  const firestoreConnect = [];
  if (isProfileCompany) {
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      storeAs: 'profileCompany',
    });
    firestoreConnect.push({
      collection: 'users',
      where: [['company', '==', profile.company]],
      orderBy: currentProfileCompanyUsersSortName &&
        currentProfileCompanyUsersSortType && [
          [
            currentProfileCompanyUsersSortName,
            currentProfileCompanyUsersSortType,
          ],
        ],
      limit: currentProfileCompanyUsersLimit,
      storeAs: 'profileCompanyUsers',
    });
  }
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    dispatch(userEditInit());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderUserAvatar = (user) => {
    return (
      <AvatarHolder
        theme={theme.profileManageUsers.avatarHolder}
        title={user.full_name ? `${user.full_name}, ${user.email}` : user.email}
      >
        {user.avatar ? (
          <Avatar
            theme={theme.profileManageUsers.avatar}
            src={user.avatar}
            alt={
              user.full_name ? `${user.full_name}, ${user.email}` : user.email
            }
          ></Avatar>
        ) : (
          <AvatarPlaceholder theme={theme.profileManageUsers.avatar} />
        )}
      </AvatarHolder>
    );
  };

  const renderUserRole = (user) => {
    const userCompanyRole =
      profileCompany &&
      profileCompany.admins &&
      profileCompany.admins.length > 0 &&
      profileCompany.admins.find((companyAdmin) => companyAdmin.id === user.id)
        ? 'isAdmin'
        : profileCompany &&
          profileCompany.users &&
          profileCompany.users.length > 0 &&
          profileCompany.users.find((companyUser) => companyUser.id === user.id)
        ? 'isUser'
        : undefined;
    const userAuthRole = roles.find(
      (role) => userCompanyRole && role.id === userCompanyRole
    );
    return (
      <>
        {isProfileCompany ? (
          isLoaded(profileCompany) ? (
            profileCompanyRole &&
            authRole &&
            authRole.privileges &&
            authRole.privileges['MANAGE_COMPANY_USER_ROLE'] ? (
              <Select
                value={userCompanyRole}
                options={
                  roles &&
                  roles.length > 0 &&
                  roles
                    .filter((role) => {
                      return (
                        (authRole.nomination &&
                          authRole.nomination.includes(role.id)) ||
                        role.id === userCompanyRole
                      );
                    })
                    .map((role) => {
                      return {
                        ...role,
                        value: role.id,
                        label: intl.formatMessage({
                          id: `ROLE.${role.name}`,
                          defaultMessage: role.name,
                        }),
                      };
                    })
                }
                placeholderMessage={'PROFILE_MANAGE_USERS.INPUT.ROLE'}
                onChange={(value) => {
                  handleChangeRole(user, value);
                }}
                small
              />
            ) : userCompanyRole ? (
              <Input
                theme={theme.input.default}
                placeholder={intl.formatMessage({
                  id: 'PROFILE_MANAGE_USERS.INPUT.ROLE',
                })}
                disabled={true}
                readOnly={true}
                value={intl.formatMessage({
                  id: `ROLE.${userAuthRole.name}`,
                  defaultMessage: userAuthRole.name,
                })}
              />
            ) : (
              <Inputbox>
                <Input
                  theme={theme.input.default}
                  placeholder={intl.formatMessage({
                    id: 'PROFILE_MANAGE_USERS.INPUT.ROLE',
                  })}
                  disabled={true}
                  readOnly={true}
                  value={intl.formatMessage({
                    id: `PROFILE_MANAGE_USERS.ROLE.NO_ROLE`,
                  })}
                />
              </Inputbox>
            )
          ) : (
            <Inputbox>
              <Input
                theme={theme.input.default}
                placeholder={intl.formatMessage({
                  id: 'PROFILE_MANAGE_USERS.INPUT.ROLE',
                })}
                disabled={true}
                readOnly={true}
                value={intl.formatMessage({
                  id: `PROFILE_MANAGE_USERS.ROLE.LOADING`,
                })}
              />
            </Inputbox>
          )
        ) : (
          ''
        )}
      </>
    );
  };

  const renderUserLastActivity = (user) => {
    return (
      <>
        {user.loggedIn ? (
          <DateLabel>
            <Strong>{moment(user.loggedIn).format('YYYY-MM-DD')}</Strong>

            {moment(user.loggedIn).format('HH:mm:ss')}
          </DateLabel>
        ) : (
          intl.formatMessage({
            id: 'PROFILE_MANAGE_USERS.NO_DATA',
          })
        )}
      </>
    );
  };

  return (
    <>
      <Header theme={theme.profileManageUsers.header}>
        <Title theme={theme.profileManageUsers.title}>
          {intl.formatMessage({
            id: 'PROFILE_MANAGE_USERS.USERS',
          })}
        </Title>
      </Header>
      <Content>
        <Box>
          <Aside>
            <ProfileManageAsideTabs
              currentTab={currentTab}
              tabs={tabs}
              handleChangeTab={handleChangeTab}
            />
          </Aside>
          <Main>
            <MainInner>
              <Subtitle theme={theme.profileManageUsers.subtitle}>
                {intl.formatMessage({
                  id: 'PROFILE_MANAGE_USERS.USERS_OF_VINNE',
                })}
                {profileCompanyUsers && (
                  <Deco theme={theme.profileManageUsers.deco}>
                    {profileCompanyUsers.length}
                  </Deco>
                )}
              </Subtitle>
              {isLoaded(profileCompanyUsers) ? (
                profileCompanyUsers && profileCompanyUsers.length > 0 ? (
                  <>
                    <Table>
                      {width > Breakpoints.sm ? (
                        <Row theme={theme.profileManageUsers.row.head} head>
                          <Cell
                            theme={theme.profileManageUsers.cell.head}
                            head
                          ></Cell>
                          <Cell
                            theme={theme.profileManageUsers.cell.head}
                            head
                            active={
                              currentProfileCompanyUsersSortName ===
                                'full_name' &&
                              currentProfileCompanyUsersSortType
                                ? true
                                : undefined
                            }
                            onClick={() => {
                              handleSetCurrentSort('full_name');
                            }}
                          >
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_USERS.NAME',
                            })}{' '}
                            <SortSign
                              theme={theme.profileManageUsers.sortSign}
                              active={
                                currentProfileCompanyUsersSortName ===
                                  'full_name' &&
                                currentProfileCompanyUsersSortType
                                  ? true
                                  : undefined
                              }
                            >
                              {((currentProfileCompanyUsersSortName ===
                                'full_name' &&
                                (currentProfileCompanyUsersSortType === 'asc' ||
                                  !currentProfileCompanyUsersSortType)) ||
                                currentProfileCompanyUsersSortName !==
                                  'full_name') && (
                                <SortSignIcon className='material-icons'>
                                  arrow_drop_up
                                </SortSignIcon>
                              )}
                              {((currentProfileCompanyUsersSortName ===
                                'full_name' &&
                                (currentProfileCompanyUsersSortType ===
                                  'desc' ||
                                  !currentProfileCompanyUsersSortType)) ||
                                currentProfileCompanyUsersSortName !==
                                  'full_name') && (
                                <SortSignIcon className='material-icons'>
                                  arrow_drop_down
                                </SortSignIcon>
                              )}
                            </SortSign>
                          </Cell>
                          <Cell
                            theme={theme.profileManageUsers.cell.head}
                            head
                            active={
                              currentProfileCompanyUsersSortName === 'email' &&
                              currentProfileCompanyUsersSortType
                                ? true
                                : undefined
                            }
                            onClick={() => {
                              handleSetCurrentSort('email');
                            }}
                          >
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_USERS.EMAIL',
                            })}{' '}
                            <SortSign
                              theme={theme.profileManageUsers.sortSign}
                              active={
                                currentProfileCompanyUsersSortName ===
                                  'email' && currentProfileCompanyUsersSortType
                                  ? true
                                  : undefined
                              }
                            >
                              {((currentProfileCompanyUsersSortName ===
                                'email' &&
                                (currentProfileCompanyUsersSortType === 'asc' ||
                                  !currentProfileCompanyUsersSortType)) ||
                                currentProfileCompanyUsersSortName !==
                                  'email') && (
                                <SortSignIcon className='material-icons'>
                                  arrow_drop_up
                                </SortSignIcon>
                              )}
                              {((currentProfileCompanyUsersSortName ===
                                'email' &&
                                (currentProfileCompanyUsersSortType ===
                                  'desc' ||
                                  !currentProfileCompanyUsersSortType)) ||
                                currentProfileCompanyUsersSortName !==
                                  'email') && (
                                <SortSignIcon className='material-icons'>
                                  arrow_drop_down
                                </SortSignIcon>
                              )}
                            </SortSign>
                          </Cell>
                          <Cell theme={theme.profileManageUsers.cell.head} head>
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_USERS.ROLE',
                            })}
                          </Cell>
                          <Cell
                            theme={theme.profileManageUsers.cell.head}
                            head
                            active={
                              currentProfileCompanyUsersSortName ===
                                'loggedIn' && currentProfileCompanyUsersSortType
                                ? true
                                : undefined
                            }
                            onClick={() => {
                              handleSetCurrentSort('loggedIn');
                            }}
                          >
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_USERS.LAST_ACTIVITY',
                            })}{' '}
                            <SortSign
                              theme={theme.profileManageUsers.sortSign}
                              active={
                                currentProfileCompanyUsersSortName ===
                                  'loggedIn' &&
                                currentProfileCompanyUsersSortType
                                  ? true
                                  : undefined
                              }
                            >
                              {((currentProfileCompanyUsersSortName ===
                                'loggedIn' &&
                                (currentProfileCompanyUsersSortType === 'asc' ||
                                  !currentProfileCompanyUsersSortType)) ||
                                currentProfileCompanyUsersSortName !==
                                  'loggedIn') && (
                                <SortSignIcon className='material-icons'>
                                  arrow_drop_up
                                </SortSignIcon>
                              )}
                              {((currentProfileCompanyUsersSortName ===
                                'loggedIn' &&
                                (currentProfileCompanyUsersSortType ===
                                  'desc' ||
                                  !currentProfileCompanyUsersSortType)) ||
                                currentProfileCompanyUsersSortName !==
                                  'loggedIn') && (
                                <SortSignIcon className='material-icons'>
                                  arrow_drop_down
                                </SortSignIcon>
                              )}
                            </SortSign>
                          </Cell>
                          <Cell
                            theme={theme.profileManageUsers.cell.head}
                            head
                          ></Cell>
                        </Row>
                      ) : (
                        ''
                      )}
                      {[...profileCompanyUsers].map((user, key) => {
                        return (
                          <Row
                            theme={theme.profileManageUsers.row.default}
                            key={key}
                          >
                            <Cell theme={theme.profileManageUsers.cell.default}>
                              {width <= Breakpoints.sm ? (
                                <Label>
                                  {intl.formatMessage({
                                    id: 'PROFILE_MANAGE_USERS.AVATAR',
                                  })}
                                </Label>
                              ) : (
                                ''
                              )}
                              {width <= Breakpoints.sm ? (
                                <Value>{renderUserAvatar(user)}</Value>
                              ) : (
                                renderUserAvatar(user)
                              )}
                            </Cell>
                            <Cell theme={theme.profileManageUsers.cell.default}>
                              {width <= Breakpoints.sm ? (
                                <Label>
                                  {intl.formatMessage({
                                    id: 'PROFILE_MANAGE_USERS.NAME',
                                  })}
                                </Label>
                              ) : (
                                ''
                              )}
                              {width <= Breakpoints.sm ? (
                                <Value>{user.full_name}</Value>
                              ) : (
                                user.full_name
                              )}
                            </Cell>
                            <Cell theme={theme.profileManageUsers.cell.default}>
                              {width <= Breakpoints.sm ? (
                                <Label>
                                  {intl.formatMessage({
                                    id: 'PROFILE_MANAGE_USERS.EMAIL',
                                  })}
                                </Label>
                              ) : (
                                ''
                              )}
                              {width <= Breakpoints.sm ? (
                                <Value>{user.email}</Value>
                              ) : (
                                user.email
                              )}
                            </Cell>
                            <Cell theme={theme.profileManageUsers.cell.default}>
                              {width <= Breakpoints.sm ? (
                                <Label>
                                  {intl.formatMessage({
                                    id: 'PROFILE_MANAGE_USERS.ROLE',
                                  })}
                                </Label>
                              ) : (
                                ''
                              )}
                              {width <= Breakpoints.sm ? (
                                <Value>{renderUserRole(user)}</Value>
                              ) : (
                                renderUserRole(user)
                              )}
                            </Cell>

                            <Cell theme={theme.profileManageUsers.cell.default}>
                              {width <= Breakpoints.sm ? (
                                <Label>
                                  {intl.formatMessage({
                                    id: 'PROFILE_MANAGE_USERS.LAST_ACTIVITY',
                                  })}
                                </Label>
                              ) : (
                                ''
                              )}
                              {width <= Breakpoints.sm ? (
                                <Value>{renderUserLastActivity(user)}</Value>
                              ) : (
                                renderUserLastActivity(user)
                              )}
                            </Cell>
                            <Cell
                              theme={theme.profileManageUsers.cell.default}
                              right
                            >
                              <ActionButton
                                theme={theme.button.default}
                                title={intl.formatMessage({
                                  id: 'PROFILE_MANAGE_USERS.DELETE',
                                })}
                                small
                                square
                                onClick={() => {
                                  handleAction({
                                    type: 'DELETE',
                                    payload: user,
                                  });
                                }}
                              >
                                <ActionButtonIcon className={'material-icons'}>
                                  delete_outline
                                </ActionButtonIcon>
                              </ActionButton>
                            </Cell>
                          </Row>
                        );
                      })}
                    </Table>
                    {currentProfileCompanyUsersLimit <=
                    profileCompanyUsers.length ? (
                      <MoreBar>
                        <More
                          theme={theme.more}
                          onClick={() =>
                            handleProfileCompanyUsersLoadingMoreData()
                          }
                        >
                          {intl.formatMessage({
                            id: 'PROFILE_MANAGE_USERS.SEE_MORE',
                          })}{' '}
                          <MoreIcon className='material-icons'>
                            keyboard_arrow_down
                          </MoreIcon>
                        </More>
                      </MoreBar>
                    ) : (
                      ''
                    )}
                  </>
                ) : (
                  <NoResults
                    label={intl.formatMessage({
                      id: 'PROFILE_MANAGE_USERS.NO_USERS',
                    })}
                  />
                )
              ) : (
                <LoadingData fullmode />
              )}
            </MainInner>
          </Main>
        </Box>
      </Content>
      <Footer>
        <Button
          type='button'
          theme={theme.button.alt}
          onClick={() => {
            handleClose();
          }}
        >
          {intl.formatMessage({ id: 'PROFILE_MANAGE_USERS.CANCEL' })}
        </Button>
        <Button
          type='button'
          theme={theme.button.default}
          onClick={() => {
            handleAction({ type: 'INVITE' });
          }}
        >
          {intl.formatMessage({
            id: 'PROFILE_MANAGE_USERS.INVITE_USERS',
          })}
        </Button>
      </Footer>
      {visibleProfileManageUsersInviteUsersModal && (
        <ProfileManageUsersInviteUsers
          handleClose={() => {
            handleAction();
          }}
        />
      )}
      {visibleProfileManageUsersManageUserModal && (
        <ProfileManageUsersManageUser
          data={selectedUser}
          handleClose={() => {
            handleAction();
          }}
        />
      )}
    </>
  );
};

ProfileManageUsers.propTypes = {
  currentTab: PropTypes.string,
  tabs: PropTypes.array,
  handleClose: PropTypes.func,
  handleChangeTab: PropTypes.func,
};

export default ProfileManageUsers;
